import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL } from '../lib/utils';
import {
  type EventMapDrawing,
  deleteEventMapDrawingsApi,
  fetchEventMapDrawings,
  migrateLocalMapDrawingsToServer,
  saveEventMapDrawingsApi,
} from '../lib/mapDrawings';

interface UseEventMapDrawingsOptions {
  eventId: string | null | undefined;
  token: string | null | undefined;
  /** When true, subscribe to socket updates (default true). */
  live?: boolean;
  /** When true, migrate localStorage drawings if server is empty (default true). */
  migrateLocal?: boolean;
}

export function useEventMapDrawings({
  eventId,
  token,
  live = true,
  migrateLocal = true,
}: UseEventMapDrawingsOptions) {
  const [drawings, setDrawings] = useState<EventMapDrawing[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastSavedJsonRef = useRef<string>('');

  const reload = useCallback(async () => {
    if (!eventId || !token) {
      setDrawings([]);
      lastSavedJsonRef.current = '[]';
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (migrateLocal) {
        const migrated = await migrateLocalMapDrawingsToServer(eventId, token);
        if (migrated) {
          setDrawings(migrated);
          lastSavedJsonRef.current = JSON.stringify(migrated);
          return;
        }
      }

      const next = await fetchEventMapDrawings(eventId, token);
      setDrawings(next);
      lastSavedJsonRef.current = JSON.stringify(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load map drawings');
      setDrawings([]);
      lastSavedJsonRef.current = '[]';
    } finally {
      setLoading(false);
    }
  }, [eventId, token, migrateLocal]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!live || !eventId || !token) return;

    const socket: Socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    const onDrawingsUpdated = (payload: {
      eventId: string;
      drawings: EventMapDrawing[];
    }) => {
      if (payload.eventId !== eventId) return;
      const incoming = Array.isArray(payload.drawings) ? payload.drawings : [];
      const incomingJson = JSON.stringify(incoming);
      if (incomingJson === lastSavedJsonRef.current) return;
      setDrawings(incoming);
      lastSavedJsonRef.current = incomingJson;
    };

    socket.on('event:map-drawings-updated', onDrawingsUpdated);
    socket.on('event:updated', (event: { id: string; mapDrawings?: EventMapDrawing[] }) => {
      if (event.id !== eventId) return;
      const incoming = Array.isArray(event.mapDrawings) ? event.mapDrawings : [];
      const incomingJson = JSON.stringify(incoming);
      if (incomingJson === lastSavedJsonRef.current) return;
      setDrawings(incoming);
      lastSavedJsonRef.current = incomingJson;
    });

    return () => {
      socket.off('event:map-drawings-updated', onDrawingsUpdated);
      socket.disconnect();
    };
  }, [live, eventId, token]);

  const saveDrawings = useCallback(
    async (nextDrawings: EventMapDrawing[]) => {
      if (!eventId || !token) return null;

      setSaving(true);
      setError(null);
      try {
        const saved = await saveEventMapDrawingsApi(eventId, token, nextDrawings);
        setDrawings(saved);
        lastSavedJsonRef.current = JSON.stringify(saved);
        return saved;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to save map drawings';
        setError(message);
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [eventId, token]
  );

  const clearDrawings = useCallback(async () => {
    if (!eventId || !token) return;
    setSaving(true);
    setError(null);
    try {
      await deleteEventMapDrawingsApi(eventId, token);
      setDrawings([]);
      lastSavedJsonRef.current = '[]';
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to clear map drawings';
      setError(message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [eventId, token]);

  return {
    drawings,
    loading,
    saving,
    error,
    reload,
    saveDrawings,
    clearDrawings,
    setDrawingsLocal: setDrawings,
  };
}
